import {useUserStore} from '../stores/user-store';
import {loggerFactory} from '../logger'
import { loadChunkWithRetry, preloadAuthChunks } from './chunk-loader';

// Static imports for auth layout and components to prevent chunk corruption
import AuthLayout from 'layouts/AuthLayout.vue';
import LoginPage from 'pages/Auth/LoginPage.vue';
import RegisterPage from 'pages/Auth/RegisterPage.vue';
import ForgotPage from 'pages/Auth/ForgotPage.vue';
import ConfirmPage from 'pages/Auth/ConfirmPage.vue';
import RetrieveCheckoutLinkPage from 'pages/Auth/RetrieveCheckoutLinkPage.vue';
import OAuthConsentPage from 'pages/Auth/OAuthConsentPage.vue';

// Preload auth chunks on module load
preloadAuthChunks();

const logger = loggerFactory("Routes")
const requireAuth = async (to, from, next) => {
  logger.debug("Checking auth for route", from, to)
  return useUserStore()
    .currentUser()
    .then((user) => {
      if (user) {
        logger.debug("User validated...continue", user);
        next();
      } else {
        logger.debug("User not validated...login");
        next({ path: "/auth/login", query: { nextUrl: encodeURIComponent(to.fullPath) } });
      }
    })
    .catch((e) => {
      logger.error("Failure processing current user", e)
      next({ path: "/auth/login", query: { nextUrl: encodeURIComponent(to.fullPath) } });
    });
};

const routes = [
  {
    path: '/home',

    component: () => loadChunkWithRetry(() => import('layouts/HomeLayout.vue')),
    children: [
      {path: '', component: () => loadChunkWithRetry(() => import('pages/HomePage.vue'))}
    ]
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {path: 'login', component: LoginPage, alias: ['/login-forgot']},
      {
        path: 'register/:referral?',
        component: RegisterPage,
        alias: ['/signup/:referral?', '/get-started/:referral?']
      },
      {path: 'forgot', component: ForgotPage},
      {path: 'confirm', component: ConfirmPage},
      {path: 'retrieve-checkout-link', component: RetrieveCheckoutLinkPage}
    ]
  },
  {
    path: '/oauth',
    component: AuthLayout,
    children: [
      {path: 'consent', component: OAuthConsentPage}
    ]
  },
  {
    path: '/',
    component: () => loadChunkWithRetry(() => import('layouts/MainLayout.vue')),
    children: [
      {
        path: '',
        component: () => loadChunkWithRetry(() => import('pages/ExchangePage.vue')),
        beforeEnter: requireAuth,
        meta: {loading: true},
      },
      {
        path: 'market/:symbol',
        component: () => loadChunkWithRetry(() => import('pages/ExchangePage.vue')),
        beforeEnter: requireAuth,
        meta: {loading: true}
      },
      {
        path: 'market/:symbol/contract/:contract',
        component: () => loadChunkWithRetry(() => import('pages/ExchangePage.vue')),
        beforeEnter: requireAuth,
        meta: {loading: true}
      },
      {
        path: 'top-contracts',
        component: () => loadChunkWithRetry(() => import('pages/TopContractsPage.vue')),
        beforeEnter: requireAuth,
        meta: { loading: true}
      },
      {
        path: 'alerts-today',
        component: () => loadChunkWithRetry(() => import('pages/AlertsTodayPage.vue')),
        beforeEnter: requireAuth,
        meta: {loading: true}
      },
      {
        path: 'alerts',
        component: () => loadChunkWithRetry(() => import('pages/AlertsPage.vue')),
        beforeEnter: requireAuth,
        meta: {loading: true}
      },
      {
        path: 'analytics',
        component: () => loadChunkWithRetry(() => import('pages/AnalyticsPage.vue')),
        beforeEnter: requireAuth,
        meta: {loading: true}
      },
      {
        path: 'developer',
        component: () => loadChunkWithRetry(() => import('pages/DeveloperPage.vue')),
        beforeEnter: requireAuth,
        meta: {loading: true}
      },
      {
        path: 'integrations',
        component: () => loadChunkWithRetry(() => import('pages/IntegrationsPage.vue')),
        beforeEnter: requireAuth,
        meta: { loading: true }
      }
    ]
  },
  {
    path: '/start',
    component: () => loadChunkWithRetry(() => import('layouts/MainLayout.vue')),
    children: [
      {path: '', component: () => loadChunkWithRetry(() => import('pages/StartPage.vue')), beforeEnter: requireAuth},
      {path: 'reactivate/:promotion', component: () => loadChunkWithRetry(() => import('pages/ReactivatePage.vue')), beforeEnter: requireAuth}
    ]
  },

  {
    path: '/subscribed',
    component: () => loadChunkWithRetry(() => import('layouts/MainLayout.vue')),
    children: [
      {path: '', component: () => loadChunkWithRetry(() => import('pages/SubscribedPage.vue')), beforeEnter: requireAuth}
    ]
  },

  {
    path: '/billing',
    component: () => loadChunkWithRetry(() => import('layouts/MainLayout.vue')),
    children: [
      {path: '', component: () => loadChunkWithRetry(() => import('pages/BillingPage.vue')), beforeEnter: requireAuth, meta: {loading: true}}
    ]
  },

  {
    path: '/profile',
    component: () => loadChunkWithRetry(() => import('layouts/MainLayout.vue')),
    children: [
      {path: '', component: () => loadChunkWithRetry(() => import('pages/ProfilePage.vue')), beforeEnter: requireAuth, meta: {loading: true}}
    ]
  },

  {
    path: '/reports',
    component: () => loadChunkWithRetry(() => import('layouts/MainLayout.vue')),
    children: [
      {path: '', component: () => loadChunkWithRetry(() => import('pages/ReportsPage.vue')), beforeEnter: requireAuth},
      {path: ':slug', component: () => loadChunkWithRetry(() => import('pages/ReportDetailsPage.vue')) }
    ]
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: "/:catchAll(.*)*",
    redirect: "/",
  }
]

export default routes
